/**
 * Rever diferencas entre aparelhos.
 *
 * A regra (ADR-0002): quando ha divergencia real, ninguem decide por ela em
 * silencio. Mas tambem ninguem lhe mostra JSON. Aqui ve-se, lado a lado, o que
 * cada aparelho diz — em palavras e numeros formatados — e escolhe-se.
 *
 * Nao ha aqui conhecimento de dominio nenhum: os campos vem do relatorio de
 * conflito, e a formatacao segue as convencoes da plataforma (valores em
 * centimos sao inteiros com sufixo `Cents`, datas sao ISO-8601).
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Banner,
  BigButton,
  Card,
  EmptyState,
  Screen,
  SectionHeading,
  Spinner,
  Stack,
} from '@flos/ui';
import { centsToMoney, longDate } from '@flos/shared';
import { useApp } from '../state/AppContext';
import type { ConflictForReview } from '../wiring/syncController';
import { SHELL_PATHS } from '../navigation';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}(T|$)/;

/** Nome de campo legivel. `merchantName` -> `Merchant name`. */
export function humanizeField(field: string): string {
  const withoutSuffix = field.replace(/Cents$/, '');
  const spaced = withoutSuffix
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[._-]+/g, ' ')
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Formata um valor para leitura humana, sem saber de que dominio ele e. */
export function formatValue(field: string, value: unknown): string {
  if (value === null || value === undefined || value === '') return 'sem valor';
  if (typeof value === 'boolean') return value ? 'sim' : 'não';
  // Convencao da plataforma: dinheiro e inteiro em centimos (normas de codificação).
  if (field.endsWith('Cents') && typeof value === 'number') return centsToMoney(value);
  if (typeof value === 'string' && ISO_DATE.test(value)) {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) return longDate(parsed);
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function fieldOf(record: unknown, field: string): unknown {
  if (typeof record !== 'object' || record === null) return undefined;
  return (record as Record<string, unknown>)[field];
}

export function ConflictScreen() {
  const { boot, manifests } = useApp();
  const navigate = useNavigate();

  const [conflicts, setConflicts] = useState<ConflictForReview[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    const list = await boot.sync.listConflicts();
    setConflicts(list);
  }, [boot.sync]);

  useEffect(() => {
    void load();
  }, [load]);

  const decide = async (
    conflict: ConflictForReview,
    kind: 'take_local' | 'take_server',
  ): Promise<void> => {
    setBusy(conflict.id);
    try {
      await boot.sync.resolveConflict(conflict.id, { kind });
      setDone(
        kind === 'take_local'
          ? 'Ficou o que estava neste aparelho.'
          : 'Ficou o que estava no outro aparelho.',
      );
      await load();
    } finally {
      setBusy(null);
    }
  };

  const labelOfEntity = (entity: string): string => {
    for (const manifest of manifests) {
      const found = manifest.entities.find((candidate) => candidate.name === entity);
      if (found) return found.label;
    }
    return entity;
  };

  if (conflicts === null) {
    return (
      <Screen title="Diferenças" back={() => navigate(SHELL_PATHS.HOME)}>
        <Spinner label="A procurar diferenças…" />
      </Screen>
    );
  }

  return (
    <Screen title="Diferenças" back={() => navigate(SHELL_PATHS.HOME)}>
      <Stack gap="lg">
        {done ? (
          <Banner tone="success" title="Salvo" onDismiss={() => setDone(null)}>
            {done}
          </Banner>
        ) : null}

        {conflicts.length === 0 ? (
          <EmptyState
            icon="✅"
            title="Não há nada para rever"
            description="Os seus aparelhos estão de acordo. Nada precisa da sua decisão."
          />
        ) : (
          <>
            <Banner tone="warning" title="Precisamos da sua ajuda">
              A mesma informação foi alterada em dois lugares. Escolha qual fica —
              a outra versão não se perde, fica no histórico.
            </Banner>

            {conflicts.map((conflict) => (
              <Card key={conflict.id} title={labelOfEntity(conflict.entity)} icon="⚖️">
                <Stack gap="md">
                  {conflict.divergentFields.map((field) => (
                    <Stack key={field} gap="sm">
                      <SectionHeading>{humanizeField(field)}</SectionHeading>
                      <div className="app-conflict__pair">
                        <p className="app-conflict__side">
                          <span className="app-conflict__where">Neste aparelho</span>
                          <span className="app-conflict__value">
                            {formatValue(field, fieldOf(conflict.localRecord, field))}
                          </span>
                        </p>
                        <p className="app-conflict__side">
                          <span className="app-conflict__where">No outro aparelho</span>
                          <span className="app-conflict__value">
                            {formatValue(field, fieldOf(conflict.serverRecord, field))}
                          </span>
                        </p>
                      </div>
                    </Stack>
                  ))}

                  <BigButton
                    icon="📱"
                    fullWidth
                    loading={busy === conflict.id}
                    onClick={() => void decide(conflict, 'take_local')}
                  >
                    Fica o deste aparelho
                  </BigButton>
                  <BigButton
                    icon="☁️"
                    variant="secondary"
                    fullWidth
                    disabled={busy === conflict.id}
                    onClick={() => void decide(conflict, 'take_server')}
                  >
                    Fica o do outro aparelho
                  </BigButton>
                </Stack>
              </Card>
            ))}
          </>
        )}
      </Stack>
    </Screen>
  );
}

export default ConflictScreen;
